import React, { useEffect, useLayoutEffect, useState } from 'react';
import type { FC } from 'react';

import { styled } from '@compiled/react';
import { useExpandSideNav } from '@atlaskit/navigation-system/layout/side-nav';
import { UNSAFE_useMediaQuery as useMediaQuery } from '@atlaskit/primitives/responsive/use-media-query';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useSSRPlaceholderReplaceIdProp, LoadableAfterPaint } from '@confluence/loadable';
import { ContextualCreateContentPopoverLoader } from '@confluence/create-blank-fabric-page/entry-points/ContextualCreateContentPopover.ts';
import { useIsCreationDropdownEnabled } from '@confluence/content-types-utils/entry-points/useIsCreationDropdownEnabled.ts';
import { useIsMultiSelectActive } from '@confluence/content-tree-actions/entry-points/useIsMultiSelectActive.ts';
import {
	MultiSelectMode,
	useContentTreeSelection,
} from '@confluence/content-tree-actions/entry-points/store.ts';
import type { SetSelection } from './SpaceViewsMenu.tsx';
import { MultiSelectButton, ExitMultiSelectButton } from './MultiSelectButton.tsx';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ActionsContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& #multi-select-button, & #contextual-create-content-button': {
		width: '24px',
		height: '24px',
		minHeight: '24px',
		minWidth: '24px',
	},
});

const SpaceViewsMenuLoader = LoadableAfterPaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-SpaceViewsMenu" */ './SpaceViewsMenu.tsx'))
			.SpaceViewsMenu,
});

type PagesItemActionsProps = {
	isLicensed: boolean;
	edition?: string;
	selection: string;
	setSelection: SetSelection;
	spaceKey: string;
	expandContentSection?: () => void;
	onItemClick?: () => void;
	spaceHasUserContent: boolean;
	pageTreeFinishedLoading: boolean;
	canManageContentOrSuperAdmin: boolean;
	spaceViewsMenuTriggerRef?: React.RefObject<HTMLButtonElement>;
};

export const PagesItemActions: FC<PagesItemActionsProps> = ({
	isLicensed,
	edition,
	selection,
	setSelection,
	spaceKey,
	expandContentSection,
	onItemClick,
	spaceHasUserContent,
	pageTreeFinishedLoading,
	canManageContentOrSuperAdmin,
	spaceViewsMenuTriggerRef,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const canShowFolder = selection === 'TREE';
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const isCreationADropdown = useIsCreationDropdownEnabled();
	const [isContextualCreateFocused, setIsContextualCreateFocused] = useState<boolean>(false);
	const [isContextualCreatePopupOpen, setIsContextualCreatePopupOpen] = useState<boolean>(false);
	const [{ multiSelectMode }, { enterMultiSelect, exitMultiSelect }] = useContentTreeSelection();
	const [isMultiSelectActive] = useIsMultiSelectActive();
	const [isMobile, setIsMobile] = useState<boolean>(false);
	const mediaQuery = useMediaQuery('below.md', function onChange(event) {
		// shifting between mobile and desktop
		if (isMultiSelectActive) {
			exitMultiSelect();
		}
		setIsMobile(event.matches);
	});

	useLayoutEffect(() => {
		setIsMobile(mediaQuery?.matches ?? false);
	}, [mediaQuery]);

	const isMultiSelectEnabled = canManageContentOrSuperAdmin && edition === 'PREMIUM';

	const expandSideNav = useExpandSideNav();
	useEffect(() => {
		return () => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: 'contentTreeBulkActions',
					action: 'closed',
					actionSubject: 'bulkActionsMenu',
					attributes: {
						method: 'unmount',
					},
				},
			}).fire();
			exitMultiSelect();
		};
	}, [createAnalyticsEvent, exitMultiSelect]);

	const spaceViewsMenu = isLicensed ? (
		<SpaceViewsMenuLoader
			selection={selection}
			setSelection={setSelection}
			isWhiteboardBetaEnabled={isCreationADropdown}
			edition={edition}
			canManageContentOrSuperAdmin={canManageContentOrSuperAdmin}
			triggerRef={spaceViewsMenuTriggerRef}
			isCompact
		/>
	) : null;

	const contextualCreate = (
		<>
			<ContextualCreateContentPopoverLoader
				isContextualCreateFocused={isContextualCreateFocused}
				setIsContextualCreateFocused={setIsContextualCreateFocused}
				shouldShowPopup={isContextualCreatePopupOpen}
				setShouldShowPopup={setIsContextualCreatePopupOpen}
				spaceKey={spaceKey}
				source="spaceLevelContextualCreate"
				rootCreateButtonStyling
				onItemClick={onItemClick}
				shouldRenderToParent
				hideFolder={!canShowFolder}
			/>
		</>
	);

	const onMultiSelectClick = () => {
		expandSideNav();
		expandContentSection?.();
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'contentTreeBulkActions',
				action: 'opened',
				actionSubject: 'bulkActionsMenu',
				actionSubjectId: 'multiSelectButton',
			},
		}).fire();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'contentTreeBulkActions',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'multiSelectButton',
				attributes: {
					spaceKey,
				},
			},
		}).fire();
		enterMultiSelect();
	};

	const onExitMultiSelectClick = () => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'contentTreeBulkActions',
				action: 'closed',
				actionSubject: 'bulkActionsMenu',
				attributes: {
					method: 'multiSelectExitButton',
				},
			},
		}).fire();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'contentTreeBulkActions',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'multiSelectExitButton',
			},
		}).fire();
		exitMultiSelect();
	};

	const actions = isMultiSelectActive ? (
		<ExitMultiSelectButton onClick={onExitMultiSelectClick} />
	) : (
		<>
			{isMultiSelectEnabled && (
				<MultiSelectButton
					onClick={onMultiSelectClick}
					isDisabled={multiSelectMode === MultiSelectMode.DISABLED}
					isLoading={!pageTreeFinishedLoading}
					spaceHasUserContent={spaceHasUserContent}
					isMultiSelectEnabled={isMultiSelectEnabled}
					isMobile={isMobile}
				/>
			)}
			{contextualCreate}
			{!isMultiSelectEnabled && spaceViewsMenu}
		</>
	);

	return (
		<ActionsContainer data-vc="pages-item-actions" {...ssrPlaceholderIdProp}>
			<>{actions}</>
		</ActionsContainer>
	);
};
